/**
 * HelpDrawer Component
 * 
 * @author Venkata Ranganath Samudrala
 * 
 * @description
 * A reusable Material-UI Drawer component for displaying help content, FAQs, and documentation.
 * This component provides a flexible side drawer that can render content either through an iframe
 * (for external URLs) or directly as HTML content. It supports fullscreen mode, loading states,
 * and navigation features including a back button for multi-level content browsing.
 * 
 * Features:
 * - Responsive drawer that slides in from the right
 * - Toggle between normal (45vw) and fullscreen (100vw) modes
 * - Support for both iframe-based URL content and direct HTML rendering
 * - Loading state indicator
 * - Back navigation support for nested content
 * - Custom styled links and content formatting
 * - Close and fullscreen controls in the header
 */

import React, { useEffect } from 'react';
import { Drawer, IconButton, Typography } from '@material-ui/core';
import {
    ArrowBack,
    Close as CloseIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon
} from '@material-ui/icons';

const HelpDrawer = ({
    open,
    onClose,
    title = 'FAQs',
    url,
    content,
    loading,
    onLoadComplete,
    fullScreen,
    onToggleFullScreen,
    showBackButton,
    onBackClick
}) => {
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            variant="persistent"
            PaperProps={{
                style: fullScreen
                    ? { width: '100vw', height: '100vh', maxWidth: '100vw', background: '#f5f8fa', boxShadow: '0 0 10px rgba(0,0,0,0.2)' }
                    : { width: '45vw', maxWidth: 900, background: '#f5f8fa', boxShadow: '0 0 10px rgba(0,0,0,0.2)' }
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #eee', background: '#0a3970', minHeight: 60 }}>
                <Typography variant="h6" style={{ color: 'white', fontWeight: 600, fontSize: 20, letterSpacing: 0.5, flex: 1, paddingLeft: 16 }}>
                    {title}
                </Typography>
                <IconButton 
                    onClick={onToggleFullScreen}
                    style={{ color: 'white', marginRight: 8, marginLeft: 8, display: 'flex', alignItems: 'center' }}
                    aria-label={fullScreen ? 'exit fullscreen' : 'fullscreen'}
                    title={fullScreen ? 'Exit Full Screen' : 'Full Screen'}
                >
                    {fullScreen ? <FullscreenExitIcon style={{ fontSize: 20, fontWeight: 'bold', verticalAlign: 'middle' }} /> : <FullscreenIcon style={{ fontSize: 20, fontWeight: 'bold', verticalAlign: 'middle' }} />}
                    <span style={{ color: 'white', fontWeight: 500, fontSize: 16, marginLeft: 6, verticalAlign: 'middle', lineHeight: 1 }}>
                        {fullScreen ? 'Exit Full Screen' : 'Full Screen'}
                    </span>
                </IconButton>
                <IconButton onClick={onClose} style={{ color: 'white', marginRight: 8, marginLeft: 8, display: 'flex', alignItems: 'center' }} aria-label="close drawer">
                    <CloseIcon style={{ fontSize: 20, fontWeight: 'bold', verticalAlign: 'middle' }} />
                    <span style={{ color: 'white', fontWeight: 500, fontSize: 16, marginLeft: 6, verticalAlign: 'middle', lineHeight: 1 }}>
                        Close
                    </span>
                </IconButton>
            </div>
            <div style={{ position: 'relative', width: '100%', height: 'calc(100vh - 70px)', background: 'white', display: 'flex', flexDirection: 'column', padding: 0, margin: 0, overflow: 'auto' }}>
                {loading && (
                    <Typography 
                        variant="body1" 
                        style={{ 
                            marginTop: 16, 
                            color: '#0a3970', 
                            fontWeight: 500, 
                            textAlign: 'center',
                            position: 'absolute',
                            width: '100%',
                            top: '40%',
                            zIndex: 2
                        }}
                    >
                        Please wait, the content is being loading...
                    </Typography>
                )}
                
                {/* Back button area */}
                {showBackButton && content && (
                    <div style={{ padding: '12px 16px', background: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                        <IconButton 
                            onClick={onBackClick}
                            size="small"
                            style={{ color: '#0a3970' }}
                            title="Go back"
                        >
                            <ArrowBack />
                        </IconButton>
                    </div>
                )}

                {/* Render HTML content directly or iframe */}
                {url ? (
                    <iframe
                        src={url}
                        title="Info"
                        style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
                        onLoad={onLoadComplete}
                    />
                ) : (
                    <>
                        <style>
                            {`
                                #help-content a {
                                    color: #0a3970;
                                    text-decoration: none;
                                    font-weight: 500;
                                    display: block;
                                    padding: 16px 20px;
                                    margin: 8px 0;
                                    background: #ffffff;
                                    border: 1px solid #e0e0e0;
                                    border-radius: 8px;
                                    transition: all 0.2s ease;
                                    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
                                }
                                #help-content a:hover {
                                    background: #f5f8fa;
                                    border-color: #0a3970;
                                    box-shadow: 0 2px 8px rgba(10,57,112,0.15);
                                    transform: translateX(4px);
                                }
                                #help-content ul {
                                    list-style: none;
                                    padding: 0;
                                    margin: 0;
                                }
                                #help-content li {
                                    margin: 0;
                                }
                                #help-content h1, #help-content h2, #help-content h3 {
                                    color: #0a3970;
                                    margin-bottom: 16px;
                                }
                                #help-content p {
                                    line-height: 1.6;
                                    margin-bottom: 12px;
                                }
                            `}
                        </style>
                        <div 
                            id="help-content" 
                            style={{ 
                                padding: 24, 
                                fontSize: 16, 
                                color: '#222',
                                flex: 1,
                                overflow: 'auto'
                            }} 
                            dangerouslySetInnerHTML={{ __html: content }}
                        />
                    </>
                )}
            </div>
        </Drawer>
    );
};

export default HelpDrawer;
